import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { QRCode } from 'react-qrcode-logo';
import { toast } from 'react-toastify';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import QrCode2Icon from '@mui/icons-material/QrCode2';

import { StyledContainer } from './styles';

const PAGE_PATH = '/app-guide';
const SITE_URL = (process.env.REACT_APP_BASE_URL || 'https://emed4u.com').replace(/\/+$/, '');
const FALLBACK_URL = `${SITE_URL}${PAGE_PATH}`;
// Emed4u ships two Android apps: the main patient app and a separate app for
// pairing health devices.
const MAIN_APP_URL = 'https://play.google.com/store/apps/details?id=com.emed4u.main&pcampaignid=web_share';
const DEVICE_APP_URL = 'https://play.google.com/store/apps/details?id=com.emed4u.device&pcampaignid=web_share';
// TEMPORARY: the new iPhone apps are not released yet, so both cards point at
// the existing Emed4u App Store listing. Replace each with its real link when it
// is live (an empty value shows "coming soon" instead of a store button).
const TEMP_APP_STORE_URL = 'https://apps.apple.com/in/app/emed4u/id1573147985';
const MAIN_APP_STORE_URL = TEMP_APP_STORE_URL;
const DEVICE_APP_STORE_URL = TEMP_APP_STORE_URL;
const QR_ID = 'app-guide-qr';

const apps = [
	{
		id: 'main',
		label: 'Main app',
		name: 'Emed4u',
		text: 'Your patient app: book appointments, consult doctors, keep your medical records and see your health data.',
		url: MAIN_APP_URL,
		appStoreUrl: MAIN_APP_STORE_URL,
		qrId: 'app-guide-qr-main'
	},
	{
		id: 'device',
		label: 'Device app',
		name: 'Emed4u(Koti)Devices',
		text: 'Pair your Emed4u ring, watch, ECG monitor or BP cuff over Bluetooth and sync your readings to your account.',
		url: DEVICE_APP_URL,
		appStoreUrl: DEVICE_APP_STORE_URL,
		qrId: 'app-guide-qr-device'
	}
];

const gettingStarted = [
	{
		id: 1,
		icon: <PhoneIphoneIcon />,
		title: 'Download the apps',
		text: 'Install the Emed4u app and the Emed4u(Koti)Devices app on your Android phone. Scan the QR codes below or tap the Google Play buttons.'
	},
	{
		id: 2,
		icon: <PersonAddAlt1Icon />,
		title: 'Sign up or sign in',
		text: 'Create your patient account with your mobile number or email, or sign in if you already have one.'
	},
	{
		id: 3,
		icon: <AssignmentIndIcon />,
		title: 'Complete your profile',
		text: 'Add your age, height, weight and medical history so your doctor sees the full picture.'
	},
	{
		id: 4,
		icon: <BluetoothIcon />,
		title: 'Connect your device',
		text: 'Pair your Emed4u health device over Bluetooth and your readings start syncing automatically.'
	}
];

const devices = [
	{
		id: 1,
		type: 'Smart ring',
		name: 'RM11 Ring',
		text: 'A lightweight ring that tracks your vitals around the clock, even while you sleep.',
		metrics: ['Heart rate', 'Blood pressure', 'SpO2', 'Steps', 'Sleep stages']
	},
	{
		id: 2,
		type: 'Smartwatch',
		name: 'E20 Watch',
		text: 'A smartwatch that keeps an eye on your daily health and activity from your wrist.',
		metrics: ['Blood pressure', 'Heart rate', 'SpO2', 'Steps', 'Sleep']
	},
	{
		id: 3,
		type: 'Health monitor',
		name: 'Blood Cough Monitor',
		text: 'A compact monitor for quick spot checks, including an ECG you can share with your doctor.',
		metrics: ['ECG', 'Blood pressure', 'Heart rate', 'SpO2']
	},
	{
		id: 4,
		type: 'BP monitor',
		name: 'Blood Pressure Cuff',
		text: 'A dedicated cuff for accurate blood pressure readings at home.',
		metrics: ['Blood pressure', 'Pulse']
	}
];

const connectSteps = [
	{
		id: 1,
		title: 'Charge and switch on your device',
		text: 'Make sure the device has enough battery and is turned on before you start pairing.'
	},
	{
		id: 2,
		title: 'Turn on Bluetooth and Location',
		text: 'On your phone, switch on Bluetooth and Location, and allow the Emed4u(Koti)Devices app to use them when asked.'
	},
	{
		id: 3,
		title: 'Open the Emed4u(Koti)Devices app and add a device',
		text: 'Sign in, go to the devices section and choose to add or connect a new device.'
	},
	{
		id: 4,
		title: 'Select your device from the list',
		text: 'Keep the device close to your phone. Pick it from the list of nearby devices and confirm the pairing.'
	},
	{
		id: 5,
		title: 'Let the first sync finish',
		text: 'Stay in the app until the first sync is complete. After that your readings sync whenever the app is connected.'
	},
	{
		id: 6,
		title: 'See your readings everywhere',
		text: 'Your vitals appear in the app and in "My health data" on the Emed4u website, where your doctor can review them too.'
	}
];

const tips = [
	'Keep your phone within 1-2 metres of the device while pairing and syncing.',
	'Open the Emed4u(Koti)Devices app at least once a day so your latest readings are uploaded.',
	'Wear the ring or watch snugly and stay still while a measurement is being taken.',
	'Keep both apps updated to get the latest device support and fixes.'
];

const faqs = [
	{
		id: 1,
		question: 'My device does not show up in the list. What should I do?',
		answer: 'Check that the device is charged and switched on, Bluetooth and Location are enabled on your phone, and the Emed4u(Koti)Devices app has permission to use them. Move the device closer to the phone and try again. If the device is paired with another app, disconnect it there first.'
	},
	{
		id: 2,
		question: 'My readings are not showing on the website.',
		answer: 'Open the Emed4u(Koti)Devices app on your phone while the device is nearby so it can sync, and make sure your phone is connected to the internet. Refresh the website after the sync completes.'
	},
	{
		id: 3,
		question: 'Can I use one device for two family members?',
		answer: 'No. For your safety and privacy, each device is linked to one patient profile only, so readings never mix between people. Each family member needs their own device.'
	},
	{
		id: 4,
		question: 'How do I move my device to another account?',
		answer: 'Raise an unlink request for the device from the Emed4u app. Once our team reviews and approves it, the device is freed and can be connected to a different profile.'
	},
	{
		id: 5,
		question: 'Is my health data secure?',
		answer: 'Yes. Your data is sent over encrypted connections and is only visible to you and the healthcare professionals you consult on Emed4u.'
	}
];

function AppGuide() {
	const [pageUrl, setPageUrl] = React.useState(FALLBACK_URL);

	React.useEffect(() => {
		if (typeof window !== 'undefined' && window.location) {
			setPageUrl(`${window.location.origin}${PAGE_PATH}`);
		}
	}, []);

	const copyLink = async () => {
		try {
			await navigator.clipboard.writeText(pageUrl);
			toast.success('Link copied');
		} catch (err) {
			console.error('Failed to copy: ', err);
			toast.error('Could not copy the link');
		}
	};

	const downloadQR = (id = QR_ID, fileName = 'emed4u-app-guide-qr.png') => {
		const canvas = document.getElementById(id);
		if (!canvas) return;
		const link = document.createElement('a');
		link.download = fileName;
		link.href = canvas.toDataURL('image/png');
		link.click();
	};

	const sharePage = async () => {
		if (navigator.share) {
			try {
				await navigator.share({
					title: 'Emed4u App & Device Guide',
					text: 'Learn how to use the Emed4u app and connect your health devices.',
					url: pageUrl
				});
			} catch (err) {
				// user closed the share sheet
			}
		} else {
			copyLink();
		}
	};

	return (
		<StyledContainer>
			<Helmet title={'Emed4u App & Device Guide - Get started and connect your health devices'}
				htmlAttributes={{ lang: "en" }}
				meta={[
					{
						name: `description`,
						content: 'Step-by-step guide to download the Emed4u app, set up your account and connect your RM11 Ring, E20 Watch, Blood Cough Monitor or blood pressure cuff.',
					},
					{
						name: `keywords`,
						content: 'emed4u app guide, connect health device, smart ring, smartwatch, ecg monitor, blood pressure monitor, remote patient monitoring',
					},
				]}
			/>

			{/* Hero */}
			<section className="guide-hero">
				<div className="container">
					<div className="row align-items-center">
						<div className="col-lg-7">
							<span className="hero-badge"><MonitorHeartIcon fontSize="small" /> App & Device Guide</span>
							<h1>Your health, connected in a few simple steps</h1>
							<p className="hero-text">
								Welcome to Emed4u. This guide shows you how to set up the app, connect your health
								device and share your readings with your doctor, all from your phone.
							</p>
							<div className="store-buttons">
								{apps.map(app => (
									<a key={app.id} href={app.url} target="_blank" rel="noreferrer" className="store-link">
										<img src="/assets/img/android.png" alt={`Get ${app.name} on Google Play`} />
										<span>{app.name}</span>
									</a>
								))}
							</div>
							<div className="hero-points">
								<span><CheckCircleIcon fontSize="small" /> Takes about 5 minutes</span>
								<span><CheckCircleIcon fontSize="small" /> Automatic Bluetooth sync</span>
								<span><CheckCircleIcon fontSize="small" /> Shared securely with your doctor</span>
							</div>
						</div>
						<div className="col-lg-5">
							<div className="qr-card">
								<h3><QrCode2Icon /> Scan to open this guide</h3>
								<p className="qr-sub">Point your phone camera at the code to follow along on your phone.</p>
								<div className="qr-frame">
									<QRCode
										id={QR_ID}
										value={pageUrl}
										size={220}
										ecLevel="H"
										quietZone={8}
										logoImage={"/assets/img/emedLogo.png"}
										logoWidth={56}
										logoHeight={56}
										removeQrCodeBehindLogo
										fgColor="#1C2134"
									/>
								</div>
								<div className="qr-link">{pageUrl}</div>
								<div className="qr-actions">
									<button type="button" className="guide-btn primary" onClick={() => downloadQR()}>
										<DownloadIcon /> Download QR
									</button>
									<button type="button" className="guide-btn outline" onClick={copyLink}>
										<ContentCopyIcon /> Copy link
									</button>
									<button type="button" className="guide-btn outline" onClick={sharePage}>
										<ShareIcon /> Share
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Download the apps */}
			<section className="guide-section alt" id="download-apps">
				<div className="container">
					<div className="section-heading">
						<span className="eyebrow">Download</span>
						<h2>Get both Emed4u apps</h2>
						<p>Emed4u has two apps. Install both: one for your care, one for your health devices.</p>
					</div>
					<div className="row justify-content-center">
						{apps.map(app => (
							<div className="col-md-6 col-lg-5 mb-4 d-flex" key={app.id}>
								<div className="app-card w-100">
									<span className="device-type">{app.label}</span>
									<h4>{app.name}</h4>
									<p>{app.text}</p>
									<div className="qr-frame">
										<QRCode
											id={app.qrId}
											value={app.url}
											size={180}
											ecLevel="H"
											quietZone={8}
											logoImage={"/assets/img/emedLogo.png"}
											logoWidth={44}
											logoHeight={44}
											removeQrCodeBehindLogo
											fgColor="#1C2134"
										/>
									</div>
									<p className="qr-sub">Scan with your Android phone to open {app.name} on Google Play.</p>
									<div className="store-buttons app-store-buttons">
										<a href={app.url} target="_blank" rel="noreferrer">
											<img src="/assets/img/android.png" alt={`Get ${app.name} on Google Play`} />
										</a>
										{app.appStoreUrl ? (
											<a href={app.appStoreUrl} target="_blank" rel="noreferrer">
												<img src="/assets/img/apple.png" alt={`Download ${app.name} on the App Store`} />
											</a>
										) : null}
									</div>
									{!app.appStoreUrl ? (
										<p className="coming-soon"><PhoneIphoneIcon fontSize="small" /> iPhone app coming soon on the App Store</p>
									) : null}
									<button
										type="button"
										className="guide-btn outline"
										onClick={() => downloadQR(app.qrId, `emed4u-${app.id}-app-qr.png`)}
									>
										<DownloadIcon /> Download QR
									</button>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Getting started */}
			<section className="guide-section">
				<div className="container">
					<div className="section-heading">
						<span className="eyebrow">Getting started</span>
						<h2>Set up Emed4u in 4 easy steps</h2>
						<p>No technical knowledge needed. Follow these steps once and you are ready to go.</p>
					</div>
					<div className="row">
						{gettingStarted.map(step => (
							<div className="col-md-6 col-lg-3 mb-4" key={step.id}>
								<div className="step-card">
									<span className="step-number">0{step.id}</span>
									<div className="icon-circle">{step.icon}</div>
									<h4>{step.title}</h4>
									<p>{step.text}</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Supported devices */}
			<section className="guide-section alt">
				<div className="container">
					<div className="section-heading">
						<span className="eyebrow">Supported devices</span>
						<h2>Devices that work with Emed4u</h2>
						<p>Connect any of these devices to keep track of your vitals without writing anything down.</p>
					</div>
					<div className="row">
						{devices.map(device => (
							<div className="col-md-6 col-lg-3 mb-4 d-flex" key={device.id}>
								<div className="device-card w-100">
									<span className="device-type">{device.type}</span>
									<h4>{device.name}</h4>
									<p>{device.text}</p>
									<div className="chips">
										{device.metrics.map(metric => (
											<span className="chip" key={metric}>{metric}</span>
										))}
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* How to connect */}
			<section className="guide-section">
				<div className="container">
					<div className="section-heading">
						<span className="eyebrow">Device connection</span>
						<h2>How to connect your device</h2>
						<p>Pairing is a one-time step. After that, your device syncs with the app on its own.</p>
					</div>
					<div className="row">
						<div className="col-lg-7 mb-4">
							<ol className="timeline">
								{connectSteps.map(step => (
									<li key={step.id}>
										<h5>{step.title}</h5>
										<p>{step.text}</p>
									</li>
								))}
							</ol>
						</div>
						<div className="col-lg-5">
							<div className="info-panel">
								<h5><LightbulbIcon /> Tips for a smooth sync</h5>
								<ul>
									{tips.map(tip => (
										<li key={tip}><CheckCircleIcon />{tip}</li>
									))}
								</ul>
							</div>
							<div className="info-panel notice">
								<h5><WarningAmberIcon /> One device, one profile</h5>
								<ul>
									<li><LockIcon />Each device can be linked to only one patient profile. To move it to another account, raise an unlink request from the app and our team will approve it.</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* FAQ */}
			<section className="guide-section alt">
				<div className="container">
					<div className="section-heading">
						<span className="eyebrow">Need a hand?</span>
						<h2>Common questions</h2>
					</div>
					<div className="faq-wrap">
						{faqs.map(faq => (
							<Accordion key={faq.id} disableGutters>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<p>{faq.question}</p>
								</AccordionSummary>
								<AccordionDetails>
									<p>{faq.answer}</p>
								</AccordionDetails>
							</Accordion>
						))}
					</div>
				</div>
			</section>

			{/* Help */}
			<section className="guide-section">
				<div className="container">
					<div className="help-cta">
						<div>
							<h3>Still need help?</h3>
							<p>Our support team is happy to help you set up the app or connect your device.</p>
						</div>
						<Link to="/contact-us" className="guide-btn light">
							<SupportAgentIcon /> Contact us
						</Link>
					</div>
				</div>
			</section>
		</StyledContainer>
	);
}

export default AppGuide;
